import * as React from 'react';

import { domToReact, HTMLReactParserOptions, htmlToDOM } from 'html-react-parser';
import { stateToHTML } from 'draft-js-export-html';

import './styles.scss';
import Icon from '../Icon';

import Table from '../Table';
import TableHead from '../TableHead';
import TableHeadCell from '../TableHeadCell';
import TableHeadRow from '../TableHeadRow';
import TableBody from '../TableBody';
import TableRow from '../TableRow';
import TableRowCell from '../TableRowCell';

import { convertFromRaw, RawDraftContentState } from 'draft-js';

export interface IRenderHTMLProps {
    /** HTML string to render */
    html: string | RawDraftContentState;
    /** className to use */
    className?: string;
    /** Do not wrap */
    nowrapper?: boolean;
    /** Replace config */
    config?: HTMLReactParserOptions['replace'];
}

interface IState {
}

/**
 * Renders HTML string to DOM tree. Also looks for known React components.
 */
export default class RenderHTML extends React.Component<IRenderHTMLProps, IState> {
    static displayName: string = 'RenderHTML';

    constructor (props: IRenderHTMLProps) {
        super(props);
    }

    render () {
        let code = '';
        if (typeof this.props.html === 'string') {
            code = this.props.html.replace(/(\r\n\t|\n|\r\t)/gm, '').trim();
        } else {
            code = stateToHTML(convertFromRaw(this.props.html));
        }

        const parserOptions = {
            replace: (domNode: any) => {
                if (domNode.name) {
                    // Icon

                    if (domNode.name && domNode.name === 'icon') {
                        return (
                            <Icon
                                width={parseInt(domNode.attribs.width, 10)}
                                height={parseInt(domNode.attribs.height, 10)}
                                kind={domNode.attribs.kind}
                            />
                        );
                    }

                    // Responsive table

                    if (domNode.name === 'table' && domNode.attribs.class === 'default') {
                        return (
                            <Table className={domNode.attribs.class}>
                                {domToReact(domNode.children, parserOptionsTable)}
                            </Table>
                        );
                    }
                }

                // Table cleanup (whitespace is not allowed between tags)

                if (
                    domNode.type === 'text' &&
                    domNode.parent &&
                    domNode.parent.name &&
                    (
                        domNode.parent.name === 'table' ||
                        domNode.parent.name === 'thead' ||
                        domNode.parent.name === 'tbody' ||
                        domNode.parent.name === 'tfoot' ||
                        domNode.parent.name === 'tr'
                    )
                ) {
                    return <React.Fragment/>;
                }

                if (this.props.config) {
                    return this.props.config(domNode);
                }

                return false;
            },
        };

        const parserOptionsTable = {
            replace: (domNode: any) => {
                if (domNode.name === 'thead') {
                    return (
                        <TableHead className={domNode.attribs.class}>
                            {domToReact(domNode.children, parserOptionsTableHead)}
                        </TableHead>
                    );
                }
                if (domNode.name === 'tbody') {
                    return (
                        <TableBody>
                            {domToReact(domNode.children, parserOptionsTableBody)}
                        </TableBody>
                    );
                }
                return false;
            },
        };
        const parserOptionsTableHead = {
            replace: (domNode: any) => {
                if (domNode.name === 'tr') {
                    return (
                        <TableHeadRow>
                            {domToReact(domNode.children, parserOptionsTableHead)}
                        </TableHeadRow>
                    );
                }
                if (domNode.name === 'th') {
                    return (
                        <TableHeadCell
                            className={domNode.attribs.class}
                            width={domNode.attribs['data-width'] ? parseInt(domNode.attribs['data-width'], 10) : undefined}
                            minWidth={domNode.attribs['data-min-width'] ? parseInt(domNode.attribs['data-min-width'], 10) : undefined}
                            maxWidth={domNode.attribs['data-max-width'] ? parseInt(domNode.attribs['data-max-width'], 10) : undefined}
                        >
                            {domToReact(domNode.children, parserOptions)}
                        </TableHeadCell>
                    );
                }
                return false;
            },
        };
        const parserOptionsTableBody = {
            replace: (domNode: any) => {
                if (domNode.name === 'tr') {
                    return (
                        <TableRow className={domNode.attribs.class}>
                            {domToReact(domNode.children, parserOptionsTableBody)}
                        </TableRow>
                    );
                } else if (domNode.name === 'td') {
                    return (
                        <TableRowCell className={domNode.attribs.class}>
                            {domToReact(domNode.children, parserOptions)}
                        </TableRowCell>
                    );
                }
                return false;
            },
        };

        if (this.props.nowrapper) {
            return (
                <React.Fragment>
                    {domToReact(htmlToDOM(code.replace(/>\s+</g,'><')), parserOptions)}
                </React.Fragment>
            );
        } else {
            return (
                <div className={this.props.className || 'longtext'}>
                    {domToReact(htmlToDOM(code.replace(/>\s+</g,'><')), parserOptions)}
                </div>
            );
        }
    }
}
